/**
 * Stylized Porte Saint-Louis — recognizable fortification gate.
 */
import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  TorusGeometry,
} from "three";
import { palette, stageMat, withOutline, type StageMaterial } from "../style/toon";

function mat(color: number) {
  return stageMat(color);
}

const stone = mat(palette.stone);
const stoneDark = mat(palette.stoneDark);
const roof = mat(palette.roofCopper);
const ink = mat(palette.ink);

function box(
  w: number,
  h: number,
  d: number,
  material: StageMaterial,
  x = 0,
  y = 0,
  z = 0,
) {
  const m = withOutline(new Mesh(new BoxGeometry(w, h, d), material), 1.035);
  m.position.set(x, y, z);
  return m;
}

function tower(x: number, z: number) {
  const g = new Group();
  g.add(box(7, 18, 7, stone, 0, 9, 0));
  g.add(box(8, 2.2, 8, stoneDark, 0, 19, 0));
  const cap = withOutline(
    new Mesh(new CylinderGeometry(0.2, 4.2, 5, 8), roof),
    1.05,
  );
  cap.position.y = 23;
  g.add(cap);
  // arrow slits
  g.add(box(0.6, 2.2, 0.35, ink, 0, 12, 3.6));
  g.add(box(0.6, 2.2, 0.35, ink, 0, 15, 3.6));
  g.position.set(x, 0, z);
  return g;
}

export function createPorteSaintLouisModel(): Group {
  const root = new Group();
  root.name = "porte-saint-louis-hero";
  root.userData = {
    kind: "hero-prop",
    id: "porte-saint-louis",
    source: "stylized procedural gate",
  };

  const pivot = new Group();
  pivot.name = "pivot_root";
  root.add(pivot);

  // Twin towers + curtain wall
  pivot.add(tower(-9, 0));
  pivot.add(tower(9, 0));
  pivot.add(box(12, 14, 5.5, stone, 0, 7, 0));
  pivot.add(box(14, 2.5, 6.5, stoneDark, 0, 15, 0));

  // Arch opening (void suggested by dark inset + torus)
  pivot.add(box(6.5, 8, 0.4, ink, 0, 5, 2.9));
  const arch = withOutline(
    new Mesh(new TorusGeometry(3.4, 0.55, 8, 16, Math.PI), stoneDark),
    1.04,
  );
  arch.rotation.x = Math.PI / 2;
  arch.rotation.z = Math.PI;
  arch.position.set(0, 9.2, 2.9);
  pivot.add(arch);

  // Road through
  pivot.add(box(6, 0.35, 14, mat(palette.road), 0, 0.2, 0));

  // Curtain wall stubs
  pivot.add(box(10, 10, 3.5, stone, -18, 5, 0));
  pivot.add(box(10, 10, 3.5, stone, 18, 5, 0));

  const collider = box(40, 22, 16, stone, 0, 11, 0);
  collider.visible = false;
  collider.name = "collider";
  collider.userData.collider = true;
  pivot.add(collider);

  return root;
}
